import React, {useEffect, useMemo, useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import DayPicker from "react-day-picker"
import cx from "classnames"
import {SecondaryButton} from "components/buttons"
import {Icon} from "components/Icon"
import moment from "moment"
import styles from "./CalendarPicker.module.css"
import "./CustomPanelDatePicker.css"
import {DisplayMode} from "types/common"
import {useModel} from "hooks"

type CalendarPickerProps = {
    selectedDay?: Date
    defaultDateViewed?: Date
    onDayClick?: Function
    displayMode?: DisplayMode
    meetingDateRange?: {startDate: Date; endDate: Date}
    /** Optional tighter range for which days can be selected; defaults to meetingDateRange */
    selectableDateRange?: {startDate: Date; endDate: Date}
    datesWithAvailableHours?: Set<string>
    showOutsideDays?: boolean
    isForceShowDateViewed?: boolean
    onMonthChange?: (month: Date) => void
}

export const CalendarPicker = (props: CalendarPickerProps) => {
    const {
        selectedDay,
        defaultDateViewed,
        onDayClick,
        displayMode = "normal",
        meetingDateRange,
        selectableDateRange,
        datesWithAvailableHours,
        showOutsideDays = true,
        isForceShowDateViewed = true,
        onMonthChange
    } = props
    const {t} = useTranslation(["calendar"])
    const model = useModel()
    const weekdaysShort = [
        t("calendar.weekdays.Sun"),
        t("calendar.weekdays.Mon"),
        t("calendar.weekdays.Tue"),
        t("calendar.weekdays.Wed"),
        t("calendar.weekdays.Thu"),
        t("calendar.weekdays.Fri"),
        t("calendar.weekdays.Sat")
    ]
    const pickerRef = useRef(null)
    const [selectedDate, setSelectedDate] = useState(selectedDay)
    const [dateViewed, setDateViewed] = useState(selectedDay || defaultDateViewed || new Date())
    const isPanelDisplay = displayMode === "panel"

    useEffect(() => {
        if (selectedDay) {
            setSelectedDate(selectedDay)
            if (isForceShowDateViewed) {
                setDateViewed(selectedDay)
            }
        }
    }, [selectedDay, isForceShowDateViewed])

    const onClick = (date) => {
        if (disabledDays && disabledDays(date)) {
            return
        }
        if (onDayClick) {
            setSelectedDate(date)
            onDayClick(date)
        }
    }

    const onClickPrevMonth = () => {
        if (isPrevMonthDisabled) return
        pickerRef.current.showPreviousMonth()
        const prevMonth = moment(dateViewed).subtract(1, "month").toDate()
        setDateViewed(prevMonth)
        if (onMonthChange) {
            onMonthChange(prevMonth)
        }
    }

    const onClickNextMonth = () => {
        if (isNextMonthDisabled) return
        pickerRef.current.showNextMonth()
        const nextMonth = moment(dateViewed).add(1, "month").toDate()
        setDateViewed(nextMonth)
        if (onMonthChange) {
            onMonthChange(nextMonth)
        }
    }

    const isPrevMonthDisabled = useMemo(() => {
        if (!meetingDateRange?.startDate || !meetingDateRange?.endDate) {
            return false
        }
        const meetingStart = moment(meetingDateRange.startDate).startOf("day")
        const prevMonth = moment(dateViewed).subtract(1, "month")
        const meetingStartMonth = moment(meetingStart).startOf("month")
        return prevMonth.isBefore(meetingStartMonth)
    }, [meetingDateRange, dateViewed])

    const isNextMonthDisabled = useMemo(() => {
        if (!meetingDateRange?.startDate || !meetingDateRange?.endDate) {
            return false
        }
        const meetingEnd = moment(meetingDateRange.endDate).endOf("day")
        const nextMonth = moment(dateViewed).add(1, "month")
        const meetingEndMonth = moment(meetingEnd).endOf("month")
        return nextMonth.isAfter(meetingEndMonth)
    }, [meetingDateRange, dateViewed])

    const disabledDays = (date: Date) => {
        const dateStr = moment(date).format("YYYY-MM-DD")
        const dayRange = selectableDateRange || meetingDateRange

        if (dayRange?.startDate && dayRange?.endDate) {
            if (
                moment(date).isBefore(moment(dayRange.startDate).startOf("day")) ||
                moment(date).isAfter(moment(dayRange.endDate).endOf("day"))
            ) {
                return true
            }
        }

        if (datesWithAvailableHours && !datesWithAvailableHours.has(dateStr)) {
            return true
        }

        return false
    }

    const modifiers = useMemo(() => {
        const base: Record<string, any> = {
            todaySelected: selectedDate
        }
        if (datesWithAvailableHours && datesWithAvailableHours.size > 0) {
            base.hasAvailableHours = (date: Date) => {
                const dateStr = moment(date).format("YYYY-MM-DD")
                return datesWithAvailableHours.has(dateStr) && moment(date).isSameOrAfter(moment().startOf("day"))
            }
        }

        return base
    }, [selectedDate, datesWithAvailableHours])

    const modifiersStyles = useMemo(() => {
        const base: Record<string, React.CSSProperties> = {}

        base.hasAvailableHours = {
            backgroundColor: "#f0f9ff",
            borderRadius: "50%",
            fontWeight: 600,
            color: "#0369a1",
            zIndex: 2
        }

        return base
    }, [])

    return (
        <div className={styles.root}>
            <div className={cx(styles.indicatorWrap, {[styles.indicatorPanelWrap]: isPanelDisplay})}>
                {isPanelDisplay && (
                    <SecondaryButton
                        title=""
                        icon={
                            <Icon
                                icon="ARROW_LEFT"
                                className={styles.indicatorIcon}
                                color={isPrevMonthDisabled ? "#939393" : "var(--black-base)"}
                            />
                        }
                        className={cx(styles.indicatorIconBtn, {
                            [styles.indicatorIconBtnPanel]: isPanelDisplay
                        })}
                        onClick={onClickPrevMonth}
                        disabled={isPrevMonthDisabled}
                    />
                )}
                <div className={cx(styles.indicatorDate, {[styles.indicatorPanelDate]: isPanelDisplay})}>
                    {moment(dateViewed).format("MMMM YYYY")}
                </div>
                {!isPanelDisplay && (
                    <SecondaryButton
                        title=""
                        icon={
                            <Icon
                                icon="ARROW_LEFT"
                                className={styles.indicatorIcon}
                                color={isPrevMonthDisabled ? "#939393" : "var(--black-base)"}
                            />
                        }
                        className={cx(styles.indicatorIconBtn, {
                            [styles.indicatorIconBtnDisabled]: isPrevMonthDisabled
                        })}
                        onClick={onClickPrevMonth}
                        disabled={isPrevMonthDisabled}
                    />
                )}
                <SecondaryButton
                    title=""
                    icon={
                        <Icon
                            icon="ARROW_LEFT"
                            className={cx(styles.indicatorIcon, styles.rotate)}
                            color={isNextMonthDisabled ? "#939393" : "var(--black-base)"}
                        />
                    }
                    className={cx(styles.indicatorIconBtn, {
                        [styles.indicatorIconBtnPanel]: isPanelDisplay
                    })}
                    onClick={onClickNextMonth}
                    disabled={isNextMonthDisabled}
                />
            </div>
            <div className={isPanelDisplay ? "PanelDatePicker" : ""}>
                <DayPicker
                    className="w-full"
                    weekdaysShort={weekdaysShort}
                    showOutsideDays={showOutsideDays}
                    month={dateViewed}
                    ref={pickerRef}
                    selectedDays={selectedDate}
                    modifiers={modifiers}
                    modifiersStyles={modifiersStyles}
                    onDayClick={onClick}
                    disabledDays={disabledDays}
                    firstDayOfWeek={model.getStartWeekOn()}
                    captionElement={() => null}
                />
            </div>
        </div>
    )
}
